(ns machtfit.widgets.dropdown
  (:require
   [clojure.string :as str]
   [machtfit.widgets.badge :as badge]
   [machtfit.widgets.checkbox :as checkbox]
   [machtfit.widgets.icon :as icon]
   [machtfit.widgets.input :as input]
   [machtfit.widgets.shared :as shared]
   [reagent.core :as r]))

(defn- listbox-option
  ([option]
   (listbox-option option nil))
  ([option on-click]
   [:div.py-px
    {:class "px-1.5"
     :role "option"
     :on-click on-click}
    [:div
     {:class ["flex"
              "items-center"
              "p-2.5"
              "cursor-pointer"
              "rounded-xl"
              "text-base"
              "leading-6"
              "bg-white"
              (when on-click "hover:bg-gray-50")
              "text-gray-800"
              (when on-click "hover:text-gray-850")]}
     option]]))

(defn searchable-listbox [options]
  (let [searchstring (r/atom (or (get-in options [:items (:value options)]) ""))
        open? (r/atom false)
        mouse-over? (r/atom false)]
    (fn [{:keys [value disabled? items on-select label hint placeholder ready?]
          :or {ready? true}}]
      [:div.flex.flex-col.gap-2
       {;; https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-haspopup
        :aria-haspopup "listbox"}
       ;; Should be extracted at a later stage
       ;; the header should be replaceable by anything
       ;; [:dropdowns/listbox [dropdowns/{input,button} ...] [dropdowns/listbox ...]]
       [shared/label {:text label}]
       [:div.relative.flex
        [input/input {:hierarchy :secondary-gray
                      :value (or @searchstring value)
                      :disabled? (when disabled?
                                   disabled?)
                      :width-class "!w-full"
                      :placeholder placeholder
                      :dropdown? true
                      :trailing-icon-component [:div
                                                {:class ["absolute"
                                                         "right-0"
                                                         "top-1/4"
                                                         "cursor-pointer"
                                                         (when-not @open? "pointer-events-none")]}
                                                [icon/outline (if @open?
                                                                "chevron-up"
                                                                "chevron-down")
                                                 {:class "mr-4"
                                                  :width "w-5"
                                                  :height "h-5"}]]

                      :events {:on-focus #(reset! open? true)
                               :on-blur #(when-not @mouse-over?
                                           (reset! open? false))
                               :on-change #(reset! searchstring (-> % .-target .-value))}}]
        ;; Could be extracted on a later stage
        ;; the header should be replaceable by anything
        ;; [:dropdowns/listbox [dropdowns/header ...] [dropdowns/{listbox, menu} ...]]
        [:div.rounded-2xl.bg-white.shadow-lg.absolute.top-full.mt-1.w-full.z-10
         {:class ["py-2.5"
                  (when-not @open?
                    "hidden")]
          :on-mouse-enter #(reset! mouse-over? true)
          :on-mouse-leave #(reset! mouse-over? false)
          ;; https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/select_role
          ;; should be for the "select" a listbox role
          ;; shoud be for a "navigation" a menu role
          :role "listbox"}
         (let [match-string (.toLowerCase @searchstring)
               matching-items (into (sorted-map) (filter (fn [[_key option]]
                                                           (.includes (.toLowerCase option) match-string))
                                                         items))]
           (if ready?
             [:div
              {:class ["overflow-y-auto" "max-h-[30vh]"]}
              (for [[key option] matching-items]
                ^{:key key}
                [:div.py-px.text-left {:class "px-1.5"}
                 [listbox-option option #(do
                                           (reset! open? false)
                                           (reset! searchstring option)
                                           (on-select key))]])]
             [:div.text-xl.animate-spin "⟳"]))]]

       [shared/hint {:text hint}]])))

(defn searchable-multi-listbox
  "`options` is a seq of {:value :label}, `selected` a set of values, and
  `on-toggle` is called with a value to add or remove it."
  [_]
  (let [open? (r/atom false)
        mouse-over? (r/atom false)
        query (r/atom "")
        input-el (atom nil)
        box-classes ["flex" "items-start" "gap-1" "min-h-[34px]" "cursor-text" "rounded-lg" "border"
                     "border-gray-200" "bg-white" "px-2" "py-1" "transition-colors" "focus-within:border-brand-400"
                     "focus-within:outline-none" "focus-within:ring" "focus-within:ring-brand-500/15"]
        icon-button-classes ["inline-flex" "items-center" "shrink-0" "m-0" "p-0" "border-0" "bg-transparent"
                             "leading-none" "cursor-pointer" "text-gray-400" "hover:text-gray-600"]]
    (fn [{:keys [options selected on-toggle placeholder]}]
      (let [selected (or selected #{})
            match (str/lower-case @query)
            visible (filter #(str/includes? (str/lower-case (:label %)) match) options)
            chosen (filter #(contains? selected (:value %)) options)
            focus-input! #(some-> @input-el .focus)
            toggle! (fn [value] (on-toggle value) (focus-input!))
            close! (fn [] (reset! open? false) (reset! query ""))]
        [:div {:class ["relative" "max-w-[240px]"]}
         [:div {:class box-classes
                :on-mouse-enter #(reset! mouse-over? true)
                :on-mouse-leave #(reset! mouse-over? false)
                :on-click #(do (reset! open? true) (focus-input!))}
          [:div {:class ["flex" "flex-1" "flex-wrap" "items-center" "gap-1" "min-w-0"]}
           (into [:<>]
                 (for [{:keys [value] :as option} chosen]
                   ^{:key value}
                   [badge/pill-outline {:text (:label option)
                                        :size :sm
                                        :icon-leading "x-close"
                                        :classes ["max-w-full" "break-words" "cursor-pointer"]
                                        :inline-styles {:white-space "normal" :height "auto"}
                                        :on-click #(toggle! value)}]))
           (when (and (not @open?) (empty? selected))
             [:span {:class ["text-sm" "text-gray-400"]} (or placeholder "auswählen…")])
           (when @open?
             [:div {:class ["flex" "basis-full" "items-center" "gap-1" "min-w-0"]}
              [:input {:type "text"
                       :ref (fn [el] (reset! input-el el) (when el (.focus el)))
                       :size 1
                       :value @query
                       :placeholder (when (empty? selected) (or placeholder "auswählen…"))
                       :class ["min-w-[2rem]" "flex-1" "border-0" "bg-transparent" "p-0" "text-sm"
                               "text-gray-900" "focus:outline-none" "focus:ring-0"]
                       :on-blur #(when-not @mouse-over? (close!))
                       :on-change #(reset! query (-> % .-target .-value))}]
              (when (seq @query)
                [:button {:type "button"
                          :aria-label "Suche löschen"
                          :class icon-button-classes
                          :on-mouse-down #(.preventDefault %)
                          :on-click #(do (reset! query "") (focus-input!))}
                 [icon/outline "x-close" {:width "w-4" :height "h-4" :stroke "stroke-gray-400"}]])])]
          [:div {:class ["flex" "shrink-0" "items-center" "gap-1" "pt-0.5"]}
           [:button {:type "button"
                     :aria-label (if @open? "Schließen" "Öffnen")
                     :class icon-button-classes
                     :on-mouse-down #(.preventDefault %)
                     :on-click (fn [e]
                                 (.stopPropagation e)
                                 (if @open?
                                   (do (close!) (some-> @input-el .blur))
                                   (reset! open? true)))}
            [icon/outline (if @open? "chevron-up" "chevron-down")
             {:width "w-4" :height "h-4" :stroke "stroke-gray-400"}]]]]
         [:div {:class ["absolute" "top-full" "z-10" "mt-1" "w-full" "rounded-lg" "border" "border-gray-200"
                        "bg-white" "py-1.5" "shadow-lg" "[&_span]:text-sm" (when-not @open? "hidden")]
                :on-mouse-enter #(reset! mouse-over? true)
                :on-mouse-leave #(reset! mouse-over? false)}
          [:div {:class ["flex" "flex-col" "gap-1" "max-h-[30vh]" "overflow-y-auto" "px-2.5"]}
           (if (seq visible)
             (into [:<>]
                   (for [{:keys [value] :as option} visible]
                     ^{:key value}
                     [checkbox/checkbox
                      {:label (:label option)
                       :checked? (contains? selected value)
                       :on-click #(do (toggle! value)
                                      (when (and (= 1 (count visible))
                                                 (not (contains? selected value)))
                                        (reset! query "")))}]))
             [:div {:class ["py-1" "text-sm" "text-gray-400"]}
              "Keine passenden Ergebnisse gefunden"])]]]))))
